import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography, useTheme } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import {
    AuditAccordionSummary,
    AuditCommentTools,
    AuditEntry,
    AuditEntryBelow,
    AuditEntryBox,
    AuditEntryBoxAbove,
    AuditEntryBoxBelow,
    AuditEntryTop,
    AuditLogEntry,
    AuditTitle,
} from "slot-definitions"
import "./compare-previous-schedule-button"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { NoResults } from "lib/@components/no-results/no-results"
import { UserAvatar } from "minimals-template/components/user-avatar"
import { useUser } from "lib/user/use-user"
import { Bound } from "lib/@components/binding/Bound"
import { DATE_FORMATS } from "lib/@constants/date-format"
import noop from "lib/noop"
import { Loader } from "lib/@components/Loader"
import { If } from "lib/@components/switch"
import { useEffect, useState } from "react"
import { FormatAuditData, OrderAuditData } from "event-definitions"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import {
    AUDIT_ORDER_DRAFT_REJECTED,
    AUDIT_ORDER_DRAFT_SUBMITTED,
    SHORT_DESCRIPTION_DRAFT_REJECTED,
    SHORT_DESCRIPTION_DRAFT_SUBMITTED,
} from "routes/audit/lib/audit-constants"
import { PDF_TAB_CONTENT_PROPS } from "lib/@components/tabs/pdf-tab-content"

export const AUDIT_COMMENT_COLORS = {
    light: { approved: "success.lighter", rejected: "error.lighter" },
    dark: { approved: "success.darker", rejected: "error.darker" },
}

export function AuditLog({ setOpen }) {
    const { data, auditId: id, type, loadingData } = useBoundContext()
    const theme = useTheme()
    if (loadingData || !data) {
        return (
            <Box sx={{ width: 1, height: 1, position: "relative" }}>
                <Loader color="primary" />
            </Box>
        )
    }

    const formattedData = formatData(data, theme.palette.mode, type)
    return formattedData.length > 0 ? (
        formattedData?.map((group, i) => (
            <Bound key={i} group={group} setOpen={setOpen}>
                <AuditLogEntry.Slot key={i} group={group} index={i} type={type}>
                    <Accordion
                        {...PDF_TAB_CONTENT_PROPS}
                        key={id}
                        sx={{
                            my: "1px !important",
                            boxShadow: "inset 0px -1px 0px rgba(145, 158, 171, 0.24)",
                            "&:before": { display: "none" },
                        }}
                        defaultExpanded={true}
                        data-cy="audit-version-accordion"
                    >
                        <AuditAccordionSummary.Slot type={type}>
                            <AccordionSummary
                                expandIcon={
                                    <Iconify icon="ic:round-chevron-right" width="24px" height="24px" rotate="90deg" />
                                }
                                id={`auditAccordionTitle-${group.id}`}
                                data-cy="audit-version-accordion-summary"
                            >
                                <AuditTitle.Slot type={type} group={group}>
                                    <Typography variant={"subtitle1"}>Version {group.version}</Typography>
                                </AuditTitle.Slot>
                            </AccordionSummary>
                        </AuditAccordionSummary.Slot>
                        <AccordionDetails>
                            <AuditCommentTools.Slot group={group} id={id} type={type} index={i} />
                            {group.content.map((groupData, idx) => (
                                <AuditComment type={type} key={idx} {...groupData} />
                            ))}
                        </AccordionDetails>
                    </Accordion>
                </AuditLogEntry.Slot>
            </Bound>
        ))
    ) : (
        <NoResults />
    )
}

function AuditComment(props) {
    const { id, userId, userName, date, desc, commentColor } = props
    const { user } = useUser(userId)
    const { close = noop, flashRejection, setFlashRejection } = useBoundContext()
    const hideTitle = props?.payload?.hideTitleFromLog
    const [isActive, setIsActive] = useState(false)

    useEffect(() => {
        if (flashRejection === id) {
            setIsActive(true)
            setTimeout(() => {
                setFlashRejection("")
                setIsActive(false)
            }, 5000)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flashRejection, id])

    return (
        <Bound auditProps={props}>
            <Box
                sx={{ my: 2, display: "flex", minWidth: "100%", pb: 4 }}
                data-cy="audit-comment"
                className={`audit-comment flashable ${isActive ? "flash" : ""}`}
            >
                <Box sx={{ m: 1, mr: 2 }}>
                    <UserAvatar sx={{ width: 32, height: 32 }} user={user} />
                </Box>
                <Box sx={{ minWidth: "80%" }}>
                    <Box display="flex" alignItems="center" sx={{ my: 1 }}>
                        <TruncatedTypography
                            data-cy="audit-comment-user"
                            variant={"caption"}
                            sx={{ color: "text.secondary", maxWidth: "fit-content", flex: 1 }}
                            id={`auditUserDateInfo-${id}`}
                        >
                            {userName}
                        </TruncatedTypography>
                        <Typography
                            data-cy="audit-comment-date"
                            variant={"caption"}
                            sx={{ color: "text.secondary", flexShrink: 0 }}
                            id={`auditUserDateInfo-${id}`}
                        >
                            , {date}
                        </Typography>
                    </Box>
                    <AuditEntryBoxAbove.Slot {...props} />
                    <AuditEntryBox.Slot {...props}>
                        <Box
                            sx={{
                                p: 1.5,
                                bgcolor: commentColor,
                                borderRadius: 1,
                                wordBreak: "break-word",
                            }}
                        >
                            <AuditEntryTop.Slot {...props} />
                            <Stack direction="row" spacing={1}>
                                <If truthy={!hideTitle}>
                                    <Box flex={1}>
                                        <Typography
                                            variant={"body2"}
                                            id={`auditInfo-${id}`}
                                            sx={{ whiteSpace: "pre-line" }}
                                        >
                                            {desc}
                                        </Typography>
                                    </Box>
                                </If>
                                <Box onClick={close}>
                                    <AuditEntry.Slot {...props} />
                                </Box>
                            </Stack>
                            <AuditEntryBelow.Slot {...props} />
                        </Box>
                    </AuditEntryBox.Slot>
                    <AuditEntryBoxBelow.Slot {...props} />
                </Box>
            </Box>
        </Bound>
    )
}

function formatData(data, themeMode, type) {
    const allData = FormatAuditData(type).call(data)
    const priorityMap = {
        [SHORT_DESCRIPTION_DRAFT_REJECTED]: AUDIT_ORDER_DRAFT_REJECTED,
        [SHORT_DESCRIPTION_DRAFT_SUBMITTED]: AUDIT_ORDER_DRAFT_SUBMITTED,
    }
    const groupedData = Object.entries(groupByReferenceId(allData))
        .map(([id, content]) => {
            const version = id.split("-version:")[1]
            const contentWithTimestamps = content.map((item) => ({
                ...item,
                __timestamp: new Date(item.date).getTime(),
            }))
            const sortedContentRaw = [...contentWithTimestamps].sort((a, b) => {
                const pa = a?.payload?.auditOrder ?? priorityMap[a.shortDescription] ?? 0
                const pb = b?.payload?.auditOrder ?? priorityMap[b.shortDescription] ?? 0
                const sameGroup =
                    a?.payload?.groupReviewId && b?.payload?.groupReviewId
                        ? a.payload.groupReviewId === b.payload.groupReviewId
                        : false
                if (sameGroup && pa !== pb) return pa - pb

                return b.__timestamp - a.__timestamp
            })
            const sortedContent = sortedContentRaw.map((data) => ({
                id: data.id,
                referenceId: data.referenceId,
                userId: data.userId,
                userName: data.userName,
                date: new Date(data.date).format(DATE_FORMATS.long),
                desc: data.shortDescription,
                reason: data.payload?.reason,
                commentColor: getCommentColor(data.payload?.status, themeMode),
                payload: data.payload,
            }))

            return {
                id,
                version,
                content: sortedContent,
                latestTimestamp: sortedContentRaw[0] ? new Date(sortedContentRaw[0].date).getTime() : 0,
            }
        })
        .sort((a, b) => {
            const versionA = Number(a.version)
            const versionB = Number(b.version)
            const isNumericA = !Number.isNaN(versionA)
            const isNumericB = !Number.isNaN(versionB)
            if (isNumericA && isNumericB && versionA !== versionB) {
                return versionB - versionA
            }
            if (isNumericA && !isNumericB) return 1
            if (!isNumericA && isNumericB) return -1
            if (a.latestTimestamp !== b.latestTimestamp) {
                return b.latestTimestamp - a.latestTimestamp
            }
            return String(b.version).localeCompare(String(a.version))
        })

    const correctedPositionData = OrderAuditData.call(groupedData)

    return correctedPositionData
}

function groupByReferenceId(data) {
    return data.groupBy((d) => d.referenceId.split(".")[0])
}

export function getCommentColor(status, themeMode) {
    return AUDIT_COMMENT_COLORS[themeMode][status] || "background.neutral"
}
